import store from 'redux/store';
import { deleteQueue, openQueue, closeQueue } from 'redux/slices/lists/queuesSlice';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditQueue } from './edit/showEditQueue';

type QueueEntity = Extract<ActionEntity, { type: 'queue' }>;

const isAllQueue = (entities: readonly ActionEntity[]): entities is readonly QueueEntity[] => entities.every((entity) => entity.type === 'queue');

/**
 * Action definitions for queues. `createQueue` / `addToTransportQueue` are
 * scoped; `edit` / `delete` / `showInstances` / `viewAllocated` are contextual;
 * `open` / `close` are status-gated toggles on the single queue's `status`,
 * mirroring the old single Open/Close menu item: `open` shows only when the
 * queue is 'closed', `close` for any other (non-closed) status — so exactly one
 * is offered and a transitional status still gets Close. Open/close and delete
 * refetch after the mutation.
 */
export const queueActionDefinitions: ActionDefinitions = [
  {
    id: 'createQueue',
    scope: 'type',
    label: 'New Queue',
    resolve: (ctx) => () => void showEditQueue({ mode: 'create', createIn: ctx.createIn }).then(ctx.refresh),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllQueue(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditQueue({ mode: 'edit', id: only.id }).then(ctx.refresh);
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllQueue(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deleteQueue({ queue_id: entity.id, zone_name: entity.zoneName, queue_name: entity.name })), {
        refresh,
        confirmBody: `Are you sure you want to delete ${entities.length === 1 ? 'this queue' : `these ${entities.length} queues`}?`,
      });
    },
  },
  {
    id: 'open',
    label: 'Open',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllQueue(entities)) return 'hidden';
      if (entities.length !== 1) return 'hidden';
      const [only] = entities;
      if (only.status !== 'closed') return 'hidden';
      return () => void store.dispatch(openQueue({ queue_id: only.id, zone_name: only.zoneName, queue_name: only.name })).then(() => refresh());
    },
  },
  {
    id: 'close',
    label: 'Close',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllQueue(entities)) return 'hidden';
      if (entities.length !== 1) return 'hidden';
      const [only] = entities;
      if (only.status === 'closed') return 'hidden';
      return () => void store.dispatch(closeQueue({ queue_id: only.id, zone_name: only.zoneName, queue_name: only.name })).then(() => refresh());
    },
  },
  {
    id: 'showInstances',
    label: 'Show related Instances',
    resolve: (ctx): ActionResult => {
      const { entities, navigate } = ctx;
      if (!isAllQueue(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      if (!navigate) return 'disabled';
      const [only] = entities;
      return () =>
        navigate(
          `/automation/node_instances?queue_name=${encodeURIComponent(only.name)}&parentOnly=false&sortColumn=node_instance_id&sortOrder=asc&page=1&limit=100`
        );
    },
  },
  {
    id: 'viewAllocated',
    label: 'View Allocated Node Instances',
    resolve: (ctx): ActionResult => {
      const { entities, navigate } = ctx;
      if (!isAllQueue(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      if (!navigate) return 'disabled';
      const [only] = entities;
      return () =>
        navigate(
          `/automation/node_instances?queue_name_allocated=${encodeURIComponent(only.name)}&parentOnly=false&sortColumn=node_instance_id&sortOrder=asc&page=1&limit=100`
        );
    },
  },
  {
    id: 'addToTransportQueue',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllQueue(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'queue',
        entities.map((entity) => entity.id),
        { objectLabel: single ? `${single.zoneName} / ${single.name}` : 'Selected queues' }
      );
    },
  },
];
